import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cx from "clsx";
import { useCallback, useLayoutEffect, useMemo, useRef, useState, } from "react";
import SSRIcon from "@ingka/ssr-icon";
import CheckIcon from "@ingka/ssr-icon/paths/checkmark";
import Text from "@ingka/text";
import { useBoolean } from "../../hooks";
import { PopOver } from "../PopOver";
import { BaseSelectSelector } from "./BaseSelectSelector";
import { anchorPosition, findNextIndex, findPrevIndex, getWidth, scrollIntoView, } from "./utils";
const MAX_HEIGHT = 320;
const MIN_HEIGHT = 160;
const MAX_MIN_WIDTH = 300;
const SelectOptionComponent = ({ value, disabled = false, onClick, selectMarker, role, selected, itemRefs, index, component, }) => {
    const ref = useMemo(() => (el) => el !== null ? itemRefs.current.set(index, el) : undefined, [itemRefs, index]);
    const handlePointerDown = useCallback((e) => e.preventDefault(), []);
    const item = selected && selectMarker === "check" ? (_jsxs("div", { className: "pdpp-react-baseSelectCheckItemRow", children: [_jsx(SSRIcon, { paths: CheckIcon, className: "pdpp-react-baseSelectCheckItem" }), component] })) : (component);
    return (_jsx("li", { className: cx("pdpp-react-baseSelectListItem", {
            "pdpp-react-baseSelectListItemSelected": selected && selectMarker === "background",
            "pdpp-react-baseSelectListItemDisabled": disabled,
            "pdpp-react-baseSelectListItemDefault": !disabled,
        }), value: value, onPointerDown: handlePointerDown, onClick: onClick, role: role, ref: ref, children: _jsx("span", { className: "pdpp-react-baseSelectItem", children: item }) }));
};
export const SelectPopover = ({ anchorEl, direction, align, isOpen, listRef, options, optionHandlers, itemRefs, valueIndex, noOptionsLabel, selectMarker, onScroll, ...props }) => {
    const onDropdownPosition = useCallback((anchorElement, popOverContainerEl) => {
        if (listRef?.current === null) {
            return;
        }
        const anchorBBox = anchorElement.getBoundingClientRect();
        const { top, bottom, left, right } = anchorBBox;
        popOverContainerEl.style.minWidth = `${right - left}px`;
        popOverContainerEl.style.maxWidth = `${Math.max(right - left, MAX_MIN_WIDTH)}px`;
        const { clientHeight } = document.documentElement;
        const spaceBelow = clientHeight - bottom - 16;
        const spaceAbove = top - 16;
        let nextDirection = direction;
        if (nextDirection === "auto") {
            if (spaceBelow < MIN_HEIGHT && spaceAbove > spaceBelow) {
                nextDirection = "up";
            }
            else {
                nextDirection = "down";
            }
        }
        let verticalPosition = "bottom";
        let verticalAlignment = "top";
        let nextHeight = Math.min(spaceBelow, MAX_HEIGHT);
        if (nextDirection === "up") {
            verticalPosition = "top";
            verticalAlignment = "bottom";
            nextHeight = Math.min(spaceAbove, MAX_HEIGHT);
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
        listRef.current.style.maxHeight = `${nextHeight}px`;
        anchorPosition(anchorElement, popOverContainerEl, {
            horizontalAlignment: align,
            horizontalPosition: align,
            verticalPosition,
            verticalAlignment,
        });
    }, [align, direction, listRef]);
    if (!isOpen) {
        return null;
    }
    return (_jsx(PopOver, { anchorEl: anchorEl, onPosition: onDropdownPosition, onScroll: onScroll, ...props, children: _jsx("ul", { className: cx("pdpp-react-baseSelectList", {
                "pdpp-react-baseSelectListLeft": align === "left",
                "pdpp-react-baseSelectListRight": align === "right",
            }), role: "menu", ref: listRef, children: options.length === 0 && noOptionsLabel !== undefined ? (_jsx(Text, { className: "pdpp-react-baseSelectNoOptions", textStyle: "Body.S", tagName: "span", children: noOptionsLabel })) : (options.map((option, index) => (_jsx(SelectOptionComponent, { value: option.value, disabled: option.disabled, onClick: optionHandlers[index], role: "menuitem", selected: index === valueIndex, selectMarker: selectMarker, component: option.component, itemRefs: itemRefs, index: index }, option.value)))) }) }));
};
export var SelectKeys;
(function (SelectKeys) {
    SelectKeys["Space"] = " ";
    SelectKeys["ArrowUp"] = "ArrowUp";
    SelectKeys["ArrowDown"] = "ArrowDown";
    SelectKeys["Enter"] = "Enter";
    SelectKeys["Escape"] = "Escape";
    SelectKeys["Esc"] = "Esc";
})(SelectKeys || (SelectKeys = {}));
export const BaseSelect = ({ value, options, onChange, onBlur, width = "full", selectMarker = "check", component, disabled = false, direction = "auto", align = "left", error = "", noOptionsLabel, ...props }) => {
    const [isOpen, openPopover, closePopover, toggleOpen] = useBoolean(false);
    const [isKeyboard, setKeyboardOn, setKeyboardOff] = useBoolean(true);
    const [popupAnchorEl, setPopupAnchorEl] = useState(null);
    const optionMap = useMemo(() => new Map(options.map((option, index) => {
        return [option.value, { ...option, index }];
    })), [options]);
    const valueOption = optionMap.get(value);
    const valueIndex = valueOption !== undefined ? valueOption.index : null;
    const optionHandlers = useMemo(() => options.map((option) => (event) => {
        event.stopPropagation();
        if (option.disabled === true) {
            return;
        }
        if (option.value !== value) {
            onChange?.(option.value);
        }
        closePopover();
    }), [options, value, closePopover, onChange]);
    const arrowIndex = useRef(null);
    const listRef = useRef(null);
    const itemRefs = useRef(new Map());
    const moveArrowSelectIndex = useCallback((fromIndex, toIndex) => {
        const itemRef = fromIndex !== null ? itemRefs.current.get(fromIndex) : undefined;
        const nextItemRef = toIndex !== null ? itemRefs.current.get(toIndex) : undefined;
        if (itemRef !== undefined) {
            itemRef.classList.remove("arrow-select");
        }
        if (nextItemRef !== undefined) {
            nextItemRef.classList.add("arrow-select");
            scrollIntoView(nextItemRef);
        }
        arrowIndex.current = toIndex;
    }, []);
    const handleKeyDown = useCallback((event) => {
        const { key } = event;
        if (!(key in SelectKeys)) {
            return;
        }
        if (key === SelectKeys.Escape || key === SelectKeys.Esc) {
            if (isOpen) {
                event.preventDefault();
                closePopover();
                setKeyboardOn();
            }
            return;
        }
        event.preventDefault();
        if (!isOpen) {
            arrowIndex.current = valueIndex;
            openPopover();
            return;
        }
        switch (key) {
            case SelectKeys.ArrowDown:
            case SelectKeys.ArrowUp: {
                const findIndex = key === SelectKeys.ArrowUp ? findPrevIndex : findNextIndex;
                const nextIndex = findIndex(arrowIndex.current, options);
                moveArrowSelectIndex(arrowIndex.current, nextIndex);
                break;
            }
            case SelectKeys.Enter: {
                if (arrowIndex.current === null) {
                    closePopover();
                    setKeyboardOn();
                    break;
                }
                optionHandlers[arrowIndex.current]?.(event);
                break;
            }
        }
    }, [
        isOpen,
        valueIndex,
        moveArrowSelectIndex,
        options,
        optionHandlers,
        closePopover,
        openPopover,
        setKeyboardOn,
    ]);
    useLayoutEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        if (listRef.current === null) {
            return undefined;
        }
        const selectedItem = valueIndex !== null ? itemRefs.current.get(valueIndex) : undefined;
        if (selectedItem !== undefined) {
            scrollIntoView(selectedItem);
        }
    }, [isOpen, listRef, itemRefs, valueIndex]);
    const handleBlur = useCallback(() => {
        onBlur?.(true);
        setKeyboardOn();
        closePopover();
    }, [closePopover, onBlur, setKeyboardOn]);
    return (_jsxs("div", { style: {
            width: getWidth(width),
        }, ref: setPopupAnchorEl, onBlur: handleBlur, ...props, children: [_jsx(BaseSelectSelector, { onToggleOpen: toggleOpen, open: isOpen, onKeyDown: handleKeyDown, onPointerDown: setKeyboardOff, hasError: error.length > 0, role: "button", tabIndex: 0, disabled: disabled, visibleFocus: isKeyboard, children: component }), _jsx(SelectPopover, { anchorEl: popupAnchorEl, onScroll: closePopover, selectMarker: selectMarker, direction: direction, align: align, onKeyDown: handleKeyDown, isOpen: isOpen, valueIndex: valueIndex, options: options, optionHandlers: optionHandlers, itemRefs: itemRefs, listRef: listRef, noOptionsLabel: noOptionsLabel })] }));
};
//# sourceMappingURL=BaseSelect.js.map